import { useEffect, useRef, useState } from "react";
import type { SessionUser } from "../../../shared/auth/access";
import type { ProductionReferenceData, TerminalAdministration } from "../models";
import { productionGateway } from "../production-gateway";
import { today } from "../production-time";
import type { ProductionWorkspaceProps } from "../production-workspace-contract";

export type TerminalRepairOptions = {
  user: SessionUser; data: TerminalAdministration; reference: ProductionReferenceData; shiftCode: string; lineId: string;
  onClose: () => void; onSaved: () => Promise<void>; notify: ProductionWorkspaceProps["notify"];
};

export function useTerminalRepairWorkspace({ user, data, reference, shiftCode, lineId, onClose, onSaved, notify }: TerminalRepairOptions) {
  const line = reference.lines.find((item) => item.id === lineId) ?? null;
  const existingTerminal = data.terminals.find((item) => item.active && item.lineId === lineId) ?? null;
  const assignment = data.assignments.find((item) => item.lineId === lineId && item.shiftDate === today() && item.shiftCode === shiftCode && item.status === "ACTIVE") ?? null;
  const [terminalCode, setTerminalCode] = useState(existingTerminal?.terminalCode ?? "");
  const [supervisorPin, setSupervisorPin] = useState("");
  const [enrollmentCode, setEnrollmentCode] = useState("");
  const [saving, setSaving] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    const drawer = drawerRef.current;
    window.requestAnimationFrame(() => drawer?.querySelector<HTMLElement>("input")?.focus());
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); return; }
      if (event.key !== "Tab" || !drawer) return;
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'));
      const first = focusable[0], last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener("keydown", closeOnEscape); previousFocus?.focus(); };
  }, [onClose]);
  const submit = async () => {
    if (!line || !terminalCode.trim() || supervisorPin.length !== 4) return;
    setSaving(true);
    try {
      const normalizedCode = terminalCode.trim().toUpperCase();
      const approval = await productionGateway.approveCriticalAction({ supervisorId: user.id, pin: supervisorPin, actionScope: "TERMINAL_REPAIR", targetRef: normalizedCode });
      const terminal = await productionGateway.pairTerminal(normalizedCode, { displayName: `${line.name} Terminali`, lineId: line.id, active: true, approvalToken: approval.approvalToken });
      setEnrollmentCode(terminal.enrollmentCode);
      setSupervisorPin("");
      await onSaved();
      notify(`${line.name} terminal eşlemesi kaydedildi.`);
    } catch (error) { notify(error instanceof Error ? error.message : "Terminal eşlemesi kaydedilemedi."); }
    finally { setSaving(false); }
  };
  const copyEnrollmentCode = async () => {
    try { await navigator.clipboard.writeText(enrollmentCode); notify("Kayıt anahtarı kopyalandı."); }
    catch { notify("Kayıt anahtarı kopyalanamadı; kodu seçip elle kopyalayın."); }
  };
  return { line, assignment, terminalCode, setTerminalCode, supervisorPin, setSupervisorPin, enrollmentCode, saving, drawerRef, submit, copyEnrollmentCode };
}
